 
/* globals PROD_ENV dataLayer */
import logger from 'components/utils/logger'
import { activateSubscribingMode } from 'reduxModules/checkout'
import { seedLifetimeFromPlanList } from 'reduxModules/checkout/dispatchers'
import {
  types,
  closeBanner,
  openBanner,
  subscriptionTransactionsFetch,
  giftSubscription,
  giftSubscriptionDetail,
  gitfSubscriptionRedeem,
  subscriptionPlansLanguageFetch,
  subscriptionPlansLanguageDefault,
  bannerIsReady
} from 'reduxModules/subscriptions'
import { hideNavigation, showNavigation } from 'reduxModules/app'
import {
  cancelFreeTrial,
  revokeGiftPermission,
  revokeFreeTrialPermission,
  getUser
} from 'reduxModules/auth'
import { dispatchLogin } from 'reduxModules/auth/dispatchers'
import { initConfirmation } from 'reduxModules/confirmation'
import authTypes from 'reduxModules/auth/types'
import { navigate } from '../../../utils/navigation'
import pushGa4Event from '../../../utils/pushGa4Event'
import notifications, {
  toastError,
  toastFailure,
  toastServerError,
  toastSuccess
} from '../../../global/notifications'

export const subscriptionPlansDispatchers = (dispatch) => ({
  async selectPlan(plan) {
    pushGa4Event('GTMEvent', {
      eventCategory: 'Subscriptions',
      eventAction: 'User Selects Plan',
      eventLabel: plan.name
    })
    if (plan.name === 'Lifetime') {
      // Resolve real backend plan id by name (rank collides with Group Learning),
      // fetch the plans list if it hasn't loaded, then seed via activateSubscribingMode.
      const seeded = await dispatch(seedLifetimeFromPlanList())
      if (!seeded) return
      navigate('/checkout')
      return
    }
    dispatch(activateSubscribingMode(plan))
    navigate('/checkout')
  },
  closeBannerSubscription() {
    dispatch(closeBanner())
  },
  openBannerSubscription() {
    dispatch(openBanner())
  }
})

export const subscriptionTransactionsDispatchers = (dispatch) => ({
  loadSubscriptionTransactions: (id) =>
    dispatch(subscriptionTransactionsFetch(id))
})

export const giftSubscriptionDispatchers = (
  stateProps,
  dispatchProps,
  ownProps
) => {
  const { dispatch } = dispatchProps
  return {
    ...stateProps,
    ownProps,
    onSubmit: (data) => {
      const dispatchGiftSubscription = (data) => {
        dispatch(giftSubscription(data))
          .then(([_, res]) => {
            if (res.type === types.GIFT_SUBSCRIPTION_SUCCESS) {
              toastSuccess(notifications.giftSubscription)
              dispatch(revokeGiftPermission())
              navigate('/account')
            } else
              toastFailure(notifications.giftSubscription, res.payload)
          })
          .catch((payload) => {
            const error = payload.errors
            toastServerError(error)
          })
      }
      if (!stateProps.usedFreeTrial) {
        const confirmation = {
          title: 'Warning',
          options: [
            { resolveValue: 'accept', text: 'Accept', class: 'confirm-btn' }
          ],
          text: 'You must be a paid Subscriber to gift a subscription. Do you want to end your free trial now and pay for your subscription?'
        }

        dispatch(initConfirmation(confirmation)).on('accept', () => {
          dispatch(cancelFreeTrial())
            .then(([_, res]) => {
              if (res.type === authTypes.CANCEL_FREE_TRIAL_SUCCESS) {
                toastSuccess(notifications.freeTrialCancelled)
                dispatch(revokeFreeTrialPermission())
                dispatchGiftSubscription(data)
              } else
                toastFailure(notifications.freeTrialCancelled, res.payload)
            })
            .catch((payload) => {
              const error = payload.errors
              toastServerError(error)
            })
        })
      } else dispatchGiftSubscription(data)
    }
  }
}

export const giftSubscriptionRedeemDispatcher = (dispatch) => ({
  getDetails: (token) => {
    dispatch(giftSubscriptionDetail(token)).catch((payload) => {
      const error = payload.errors
      toastServerError(error)
    })
  },
  hideGlobalNav() {
    dispatch(hideNavigation())
  },
  showGlobalNav() {
    dispatch(showNavigation())
  },
  onSubmit(data, props) {
    const payload = {
      password: data.password1
    }
    dispatch(gitfSubscriptionRedeem(props.params.token, payload))
      .then(([_, res]) => {
        if (res.type === types.REDEEM_GIFT_SUBSCRIPTION_SUCCESS) {
          const loginPayload = {
            email: props.email,
            password: data.password1,
            recaptcha: props.recaptcha
          }
          dispatchLogin(dispatch, loginPayload, props)
          toastSuccess(notifications.redeemedGiftedSubscription)
          setTimeout(() => {
            navigate('/')
            dispatch(getUser())
          }, 2000)
        } else {
          const error = res?.payload?.value?.error
          toastError(notifications.redeemedGiftedSubscription)
          toastServerError(error)
        }
      })
      .catch((err) => {
        if (PROD_ENV) logger.error(`Redeem gift issue: ${err}`)
      })
  }
})

export const subscriptionPlansDispatcher = (dispatch) => ({
  getSubscriptionPlanLanguage() {
    return dispatch(subscriptionPlansLanguageFetch())
      .then(([_, res]) => {
        if (res.type !== types.SUBSCRIPTION_PLANS_LANGUAGE_SUCCESS) {
          dispatch(subscriptionPlansLanguageDefault())
          return undefined
        }
        const { subscriptionBannerFreeTrialOnly } = res.payload
        // The fetched settings are already in the store; failures past this
        // point must not fall through to the catch below, which would
        // overwrite them with the hardcoded defaults.
        return dispatch(getUser())
          .then(([_, userRes]) => {
            if (userRes.type === authTypes.AUTH_FETCH_USER_SUCCESS) {
              const isSubscriber = userRes?.payload?.subscription?.id
              const { usedFreeTrial } = userRes.payload

              if (subscriptionBannerFreeTrialOnly) {
                // Check if user used their free trial and have no sub to show banner
                if (!isSubscriber && !usedFreeTrial) dispatch(openBanner())
                // If flag is false, and the user is not a subscriber, display banner
              } else if (!isSubscriber) dispatch(openBanner())
            }
            // Anon user is always eligible for free trial and has no subscription, always display banner
            else dispatch(openBanner())

            dispatch(bannerIsReady())
          })
          .catch(() => {
            // Treat an unfetchable user like an anonymous visitor
            dispatch(openBanner())
            dispatch(bannerIsReady())
          })
      })
      .catch(() => {
        dispatch(subscriptionPlansLanguageDefault())
      })
  }
})
